import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, where, getDocs, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';
import { DELEGATION_STATUSES } from './useTasks';

// Fields that should be null instead of empty string
const NULLABLE_STRING_FIELDS = [
  'description',
  'dueDate',
  'delegatedToUid',
  'delegatedToName',
  'delegatedToEmail',
  'delegatedByUid',
  'delegationStatus',
  'delegationNotes',
  'followUpDate'
];

// Normalize goal data to ensure empty strings are converted to null
function normalizeGoalData(data) {
  const normalized = { ...data };
  NULLABLE_STRING_FIELDS.forEach(field => {
    if (normalized[field] === '') {
      normalized[field] = null;
    }
  });
  return normalized;
}

// Helper to check if goal is delegated
export function isGoalDelegated(goal) {
  return !!(goal?.delegatedToUid || goal?.delegatedToName);
}

// Helper to get delegate display name
export function getGoalDelegateDisplayName(goal) {
  return goal?.delegatedToName || null;
}

export function useGoals() {
  const { user, userProfile } = useAuth();
  const [myGoals, setMyGoals] = useState([]);
  const [sharedGoals, setSharedGoals] = useState([]);
  const [accountLinks, setAccountLinks] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loading, setLoading] = useState(true);

  // Refs to hold current link state (prevents stale closures in dual listeners)
  const linksAsARef = useRef([]);
  const linksAsBRef = useRef([]);
  const mergeTimeoutRef = useRef(null);

  // Merge and dedupe links from both queries (debounced to prevent double updates)
  const mergeLinks = useCallback(() => {
    // Debounce: only merge after 50ms of no changes
    if (mergeTimeoutRef.current) {
      clearTimeout(mergeTimeoutRef.current);
    }
    mergeTimeoutRef.current = setTimeout(() => {
      const allLinks = [...linksAsARef.current, ...linksAsBRef.current];
      const uniqueLinks = Array.from(
        new Map(allLinks.map((link) => [link.id, link])).values()
      );
      setAccountLinks(uniqueLinks);
    }, 50);
  }, []);

  // Listen to account links for sharing (using userA/userB queries)
  useEffect(() => {
    if (!user?.uid) {
      linksAsARef.current = [];
      linksAsBRef.current = [];
      setAccountLinks([]);
      return;
    }

    const queryA = query(
      collection(db, 'accountLinks'),
      where('userA', '==', user.uid),
      where('status', '==', 'active')
    );

    const queryB = query(
      collection(db, 'accountLinks'),
      where('userB', '==', user.uid),
      where('status', '==', 'active')
    );

    const unsubscribeA = onSnapshot(
      queryA,
      (snapshot) => {
        linksAsARef.current = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        mergeLinks();
      },
      (err) => console.error('🔥 [useGoals] accountLinks-A listener error:', err.message)
    );

    const unsubscribeB = onSnapshot(
      queryB,
      (snapshot) => {
        linksAsBRef.current = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        mergeLinks();
      },
      (err) => console.error('🔥 [useGoals] accountLinks-B listener error:', err.message)
    );

    return () => {
      unsubscribeA();
      unsubscribeB();
      if (mergeTimeoutRef.current) {
        clearTimeout(mergeTimeoutRef.current);
      }
    };
  }, [user?.uid, mergeLinks]);

  // Fetch shared goals from linked accounts
  // Query by viewableBy array-contains to match security rules
  const fetchSharedGoals = useCallback(async () => {
    if (!user?.uid || accountLinks.length === 0) {
      setSharedGoals([]);
      return;
    }

    try {
      // Query goals where I'm in viewableBy (matches security rule)
      const goalsQuery = query(
        collection(db, 'goals'),
        where('viewableBy', 'array-contains', user.uid),
        orderBy('dueDate', 'asc'),
        limit(100)
      );
      const snapshot = await getDocs(goalsQuery);

      // Build owner info from accountLinks for display
      const linkedUserInfo = {};
      for (const link of accountLinks) {
        const isUserA = link.userA === user.uid;
        const linkedUserId = isUserA ? link.userB : link.userA;
        linkedUserInfo[linkedUserId] = {
          displayName: isUserA ? link.userBDisplayName : link.userADisplayName,
          email: isUserA ? link.userBEmail : link.userAEmail
        };
      }

      const shared = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((goal) => goal.excludeFromSharing !== true)
        .map((goal) => {
          const ownerInfo = linkedUserInfo[goal.userId] || {};
          return {
            ...goal,
            isShared: true,
            owner: ownerInfo.displayName || ownerInfo.email?.split('@')[0] || 'Shared',
            ownerDisplayName: ownerInfo.displayName,
            ownerEmail: ownerInfo.email
          };
        });

      setSharedGoals(shared);
    } catch (error) {
      console.error('Error fetching shared goals:', error);
      setSharedGoals([]);
    }
  }, [user?.uid, accountLinks]);

  // Refetch shared goals when account links change
  useEffect(() => {
    fetchSharedGoals();
  }, [fetchSharedGoals]);

  // Listen to relationships where current user is a participant
  // to find linked users whose goals should be visible (when user is the creator/manager)
  useEffect(() => {
    if (!user?.uid) {
      setRelationships([]);
      return;
    }

    const q = query(
      collection(db, 'oneOnOneRelationships'),
      where('participants', 'array-contains', user.uid)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setRelationships(data);
      },
      (err) => console.error('🔥 [useGoals] relationships listener error:', err.message)
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Get linked user IDs from relationships where current user is the creator (manager)
  // and showGoals is enabled - this shows supervisees' goals to managers
  const linkedUserIds = useMemo(() => {
    return relationships
      .filter(rel => rel.status === 'active' && rel.linkedUserId && rel.showGoals !== false && rel.createdBy === user?.uid)
      .map(rel => rel.linkedUserId);
  }, [relationships, user?.uid]);

  // Query goals for current user only (security rules require ownership)
  useEffect(() => {
    if (!user?.uid) {
      setMyGoals([]);
      setLoading(false);
      return;
    }

    // Only query current user's goals (security rules enforce userId == auth.uid)
    const q = query(
      collection(db, 'goals'),
      where('userId', '==', user.uid),
      orderBy('dueDate', 'asc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const goalsData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));
        setMyGoals(goalsData);
        setLoading(false);
      },
      (err) => {
        console.error('🔥 [useGoals] goals listener error:', err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Combine my goals with shared goals
  const allGoals = useMemo(() => {
    return [...myGoals, ...sharedGoals];
  }, [myGoals, sharedGoals]);

  // All goals including from supervisees
  const goals = allGoals;

  // Get goals owned by current user only (for editing permissions)
  const ownGoals = useMemo(() => {
    return myGoals.filter(g => g.userId === user?.uid);
  }, [myGoals, user?.uid]);

  // Check if current user can edit a goal
  const canEdit = (goal) => {
    return goal?.userId === user?.uid;
  };

  const addGoal = async (data) => {
    if (!user?.uid) {
      throw new Error('User must be logged in to add goals');
    }

    const normalizedData = normalizeGoalData(data);

    await addDoc(collection(db, 'goals'), {
      ...normalizedData,
      userId: user.uid, // Multi-tenant: owner by Firebase UID
      sharedWith: [], // Future: share with manager
      completed: false,
      // Initialize delegation fields as null
      delegatedToUid: normalizedData.delegatedToUid || null,
      delegatedToName: normalizedData.delegatedToName || null,
      delegatedToEmail: normalizedData.delegatedToEmail || null,
      delegatedByUid: normalizedData.delegatedByUid || null,
      delegatedAt: normalizedData.delegatedAt || null,
      delegationStatus: normalizedData.delegationStatus || null,
      delegationNotes: normalizedData.delegationNotes || null,
      followUpDate: normalizedData.followUpDate || null,
      createdAt: serverTimestamp()
    });
  };

  const updateGoal = async (goalId, data) => {
    const normalizedData = normalizeGoalData(data);
    await updateDoc(doc(db, 'goals', goalId), normalizedData);
  };

  const deleteGoal = async (goalId) => {
    await deleteDoc(doc(db, 'goals', goalId));
  };

  const toggleCompleted = async (goalId, currentValue) => {
    const updateData = {
      completed: !currentValue
    };
    // Set completedAt when completing, clear it when uncompleting
    if (!currentValue) {
      updateData.completedAt = serverTimestamp();
    } else {
      updateData.completedAt = null;
    }
    await updateDoc(doc(db, 'goals', goalId), updateData);
  };

  // Check if goal is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate + 'T00:00:00');
    return due < today;
  };

  // Get upcoming goals (not completed, sorted by due date)
  const upcomingGoals = goals.filter(g => !g.completed);

  // Get overdue goals
  const overdueGoals = goals.filter(g => !g.completed && isOverdue(g.dueDate));

  // Get incomplete goals for 1:1 carryover display
  const incompleteGoals = goals.filter(g => !g.completed);

  // Get completed goals
  const completedGoals = goals.filter(g => g.completed);

  // Get the owner name for a goal (from relationship personName)
  const getGoalOwnerName = (goal) => {
    if (goal?.userId === user?.uid) return 'You';
    const rel = relationships.find(r => r.linkedUserId === goal?.userId);
    return rel?.personName || 'Unknown';
  };

  // Check if goal belongs to current user
  const isMyGoal = (goal) => goal?.userId === user?.uid;

  // Get owner display text for shared goals
  const getOwnerDisplayText = (goal) => {
    if (!goal?.isShared) return null;
    return goal.ownerDisplayName || goal.ownerEmail || 'Shared';
  };

  // Delegate goal using new unified delegation system
  // delegate: { uid?: string, name: string, email?: string, isExternal: boolean }
  const delegateGoal = async (goalId, delegate, options = {}) => {
    if (!user?.uid) throw new Error('User must be logged in to delegate goals');

    const { followUpDate = null, notes = null } = options;

    await updateGoal(goalId, {
      delegatedToUid: delegate.uid || null,
      delegatedToName: delegate.name,
      delegatedToEmail: delegate.email || null,
      delegatedByUid: user.uid,
      delegatedAt: serverTimestamp(),
      delegationStatus: DELEGATION_STATUSES.PENDING,
      delegationNotes: notes,
      followUpDate: followUpDate
    });
  };

  // Update delegation status (for delegates acknowledging, completing, etc.)
  const updateDelegationStatus = async (goalId, status) => {
    await updateGoal(goalId, {
      delegationStatus: status
    });
  };

  // Remove delegation
  const removeDelegation = async (goalId) => {
    await updateGoal(goalId, {
      delegatedToUid: null,
      delegatedToName: null,
      delegatedToEmail: null,
      delegatedByUid: null,
      delegatedAt: null,
      delegationStatus: null,
      delegationNotes: null,
      followUpDate: null
    });
  };

  // Get goals delegated BY current user (I delegated these to others)
  const getDelegatedByMeGoals = useCallback(() => {
    if (!user?.uid) return [];
    return goals.filter(g =>
      (g.delegatedByUid === user.uid || g.userId === user.uid) &&
      isGoalDelegated(g) &&
      !g.completed
    );
  }, [goals, user?.uid]);

  // Get goals delegated TO current user (others delegated to me)
  const getDelegatedToMeGoals = useCallback(() => {
    if (!user?.uid) return [];
    return goals.filter(g =>
      g.delegatedToUid === user.uid &&
      !g.completed
    );
  }, [goals, user?.uid]);

  // Get delegated goals (all goals that have delegation)
  const delegatedGoals = useMemo(() => {
    return goals.filter(g => isGoalDelegated(g) && !g.completed);
  }, [goals]);

  return {
    goals,
    myGoals: ownGoals,
    sharedGoals,
    upcomingGoals,
    overdueGoals,
    incompleteGoals,
    completedGoals,
    delegatedGoals,
    loading,
    addGoal,
    updateGoal,
    deleteGoal,
    toggleCompleted,
    isOverdue,
    canEdit,
    getGoalOwnerName,
    getOwnerDisplayText,
    isMyGoal,
    // Delegation methods
    delegateGoal,
    updateDelegationStatus,
    removeDelegation,
    getDelegatedByMeGoals,
    getDelegatedToMeGoals
  };
}
